import React, { useCallback, useEffect, useState } from "react";
import { RefreshCcw, Shield, Users } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PageContainer, PageHeader, StatusPill, Skeleton, relativeTime } from "./bdcUi";

/**
 * BDC Manager — Team Overview page.
 * Route: /bdc/manager/team  (visible only to bdc_manager role)
 *
 *  • Live table of each BDC agent with claimed leads, appts today, calls today, presence.
 *  • Force-availability toggle (calls PATCH /bdc/agents/:id/availability).
 */
const PRESENCE_TONES = {
  available: { tone: "emerald", label: "Available" },
  busy: { tone: "gold", label: "Busy" },
  offline: { tone: "slate", label: "Offline" },
};

const BdcManagerTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [availabilityDialog, setAvailabilityDialog] = useState(null); // agent
  const [reason, setReason] = useState("");
  const [newPresence, setNewPresence] = useState("offline");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bdc/manager/team-overview");
      setTeam(data.team || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, tick]);

  const submitAvailability = async () => {
    if (!availabilityDialog) return;
    setSaving(true);
    try {
      await api.patch(`/bdc/agents/${availabilityDialog.bdc_user_id}/availability`, {
        presence: newPresence,
        reason: reason.trim() || null,
      });
      toast.success(`Set ${availabilityDialog.full_name} to ${newPresence}`);
      setAvailabilityDialog(null); setReason("");
      setTick((n) => n + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Team overview"
        subtitle="Live snapshot of each BDC agent. Force an agent offline if they went on break without switching."
        testId="bdc-manager-team-header"
        right={<Button variant="outline" onClick={() => setTick((n) => n + 1)} data-testid="bdc-manager-refresh"><RefreshCcw size={14} /> Refresh</Button>}
      />

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <Users size={15} className="text-brand" />
          <h3 className="font-display text-base font-semibold text-slate-900">BDC agents</h3>
          <span className="text-xs text-slate-500">({team.length})</span>
        </div>
        {loading ? (
          <div className="p-4 space-y-2">{[0,1,2].map((i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : team.length === 0 ? (
          <p className="p-6 text-center text-sm text-slate-500">No BDC agents on the team yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Agent</th>
                <th className="text-left px-4 py-2 font-semibold">Presence</th>
                <th className="text-right px-4 py-2 font-semibold">Claimed open leads</th>
                <th className="text-right px-4 py-2 font-semibold">Appts booked today</th>
                <th className="text-right px-4 py-2 font-semibold">Calls today</th>
                <th className="text-left px-4 py-2 font-semibold">Last seen</th>
                <th className="text-right px-4 py-2 font-semibold">Force presence</th>
              </tr>
            </thead>
            <tbody>
              {team.map((a) => {
                const p = PRESENCE_TONES[a.presence] || PRESENCE_TONES.offline;
                return (
                  <tr key={a.bdc_user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bdc-manager-team-row-${a.bdc_user_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-900">{a.full_name}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </td>
                    <td className="px-4 py-2"><StatusPill tone={p.tone}>{p.label}</StatusPill></td>
                    <td className="px-4 py-2 text-right font-semibold">{a.claimed_open_leads}</td>
                    <td className="px-4 py-2 text-right">{a.appts_booked_today}</td>
                    <td className="px-4 py-2 text-right">{a.calls_today}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{a.last_seen_at ? relativeTime(a.last_seen_at) : "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => { setAvailabilityDialog(a); setNewPresence("offline"); setReason(""); }} data-testid={`bdc-manager-force-${a.bdc_user_id}`}>
                        <Shield size={13} /> Force
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!availabilityDialog} onOpenChange={(v) => { if (!v) setAvailabilityDialog(null); }}>
        <DialogContent data-testid="bdc-manager-force-dialog">
          <DialogHeader>
            <DialogTitle>Force presence for {availabilityDialog?.full_name}</DialogTitle>
            <DialogDescription>Overrides the agent's own presence. They'll see the manager-set value on their softphone widget next tick.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">New presence</Label>
              <select value={newPresence} onChange={(e) => setNewPresence(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="bdc-manager-force-presence">
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="offline">Offline</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reason (optional)</Label>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Agent stepped away without switching off" data-testid="bdc-manager-force-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailabilityDialog(null)}>Cancel</Button>
            <Button onClick={submitAvailability} disabled={saving} data-testid="bdc-manager-force-submit">{saving ? "Saving…" : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default BdcManagerTeam;
